import React from "react";
import styled from "styled-components";

import { Column, Container, Row } from "../../../grid";
import { theme as newTheme } from "../../../../theme/new";
import { Header, Text } from "../../../atoms";
import { device } from "../../../../utils";
import { Heading1, Section } from "../../atoms";
import { DownloadAppPanel } from "../../molecules";

import photo1 from "../../../../assets/images/events/events_facial.webp";
import photo2 from "../../../../assets/images/events/events_vip.webp";
import photo3 from "../../../../assets/images/events/events_runway.webp";
import photo4 from "../../../../assets/images/events/events_mirror.webp";
import { GetInTouchButton } from "../../pages/events/components/get-in-touch-button";
import { PartnersPanel } from "../../pages/events/components/partners-panel";

const ContentHeader = styled.div`
  background: ${newTheme.colours.ruubyBlueGrey};
  padding: 52px 0;
  width: 100%;
`;

const PopupSection = styled(Section)`
  display: inline-flex;
  padding: 32px 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px; 
  width: 100%;
`;

const StyledContainer = styled(Container)`
  max-width: 780px;
  padding: 0;
`;

const StyledHeader = styled(Header)`
  color: ${(props) => props.color || newTheme.colours.textMain};
  font-family: ${newTheme.type.fonts.heading};
  margin-top: 20px;
  font-size: 30px;
  @media ${device.mobileL} {
    margin: inherit;
    font-size: 36px;
  }
  text-align: center;
  font-style: normal;
  font-weight: 500;
  line-height: 100%; /* 36px */ `;

const HeaderSubtitle = styled(Text)`
  color: ${(props) => props.color || newTheme.colours.textMain};
  text-align: center;
  font-family: ${newTheme.type.fonts.main};
  font-size: ${newTheme.type.sizes.size16};
  margin: 0;
  @media ${device.mobileL} {
    font-size: ${newTheme.type.sizes.size20};
  }
  font-style: normal;
  line-height: 140%;
  letter-spacing: -0.5px;
`;

const Banner = styled.div`
  font-weight: bold;
  padding: 0 16px;
  margin: 20px 10px;
  max-width: 652px;
  @media ${device.mobileL} {
    padding: 0;
    margin: 20px 0;
  }
  @media ${device.desktop} {
    max-width: 782px;
  }
`;

const ImageAndText = styled.div<{ mobileInvert?: boolean }>`
  display: flex;
  flex-direction: ${(props) => props.mobileInvert ? "column-reverse" : "column"};
  @media ${device.mobileL} {
    flex-direction: unset;
  }
  align-items: center;
  gap: unset;
  @media ${device.mobileL} {
    gap: 24px;
  }
  @media ${device.desktop} {
    gap: 30px;
  }
  margin: auto;
  max-width: 100%;
`;

const EventImage = styled.div<{ image: string }>`
  width: 100%;
  @media ${device.mobileL} {
    width: 540px;
    min-width: 309px;
  }
  height: 300px;
  border-radius: 8px;
  background: url(${(props) => props.image}) lightgray 50% / cover no-repeat;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.20); 
`;

const BrandedEventImage = styled(EventImage)`
  background-position-y: top;
`;

const PopupText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  max-width: 540px;
  margin: auto;
`;

const PartnersHeading = styled(Heading1)`
  color: ${({ theme }) => theme.colours.background.white};
  text-align: center;
`;

const PartnersContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center; 
  gap: 20px;
`;

export const Events: React.FC = () => (
  <>
    <ContentHeader>
      <StyledContainer limitedWidth>
        <Row justifyContent="center">
          <Column textAlign="center">
            <StyledHeader>Events</StyledHeader>
          </Column>
        </Row>
        <Row justifyContent="center">
          <HeaderSubtitle>
            <Banner>
              Elevate your event or special occasion with the unparalleled expertise of Ruuby's beauty
              and wellness professionals.
            </Banner>
          </HeaderSubtitle>
        </Row>
        <Row justifyContent="center">
          <GetInTouchButton />
        </Row>
      </StyledContainer>
    </ContentHeader>

    <PopupSection colour="darkGrey">
      <ImageAndText>
        <EventImage image={photo1} />
        <PopupText>
          <StyledHeader color="white">Event Pop-Ups</StyledHeader>
          <HeaderSubtitle color="white">
            Transform your event into an unforgettable experience by
            incorporating Ruuby's beauty stations, offering tailored
            treatments, express services, and exclusive party add-ons for your guests.
          </HeaderSubtitle>
        </PopupText>
      </ImageAndText>
    </PopupSection>

    <PopupSection colour="blueGrey">
      <ImageAndText mobileInvert>
        <PopupText>
          <StyledHeader>VIP Client Hospitality</StyledHeader>
          <HeaderSubtitle>
            As the preferred beauty provider for VIP clients and luxury
            brands, our concierge team excels in pre-event preparations,
            gifting experiences, and personalised styling sessions, 
            ensuring a seamless and bespoke touch to every interaction.
          </HeaderSubtitle>
        </PopupText>
        <EventImage image={photo2} />
      </ImageAndText>
    </PopupSection>

    <PopupSection colour="darkGrey">
      <ImageAndText>
        <EventImage image={photo3} />
        <PopupText>
          <StyledHeader color="white">Campaigns and Shows</StyledHeader>
          <HeaderSubtitle color="white">
            Partner with our team of globally acclaimed talent who have
            extensive experience in delivering runway, editorial and 
            campaigns shoots.
          </HeaderSubtitle>
        </PopupText>
      </ImageAndText>
    </PopupSection>

    <PopupSection colour="blueGrey">
      <ImageAndText mobileInvert>
        <PopupText>
          <StyledHeader>Branded Experiences</StyledHeader>
          <HeaderSubtitle>
            Collaborate with Ruuby to create tailored treatment
            experiences to showcase your brand. From branded
            treatments to concept nail art, our team will work with you to
            execute your vision for press days, marketing activations,
            influencer appointments and large-scale events.
          </HeaderSubtitle>
        </PopupText>
        <BrandedEventImage image={photo4} />
      </ImageAndText>
    </PopupSection>

    <Section colour="darkGrey">
      <PartnersContainer>
        <Row justifyContent="center">
          <Column>
            <PartnersHeading>A Trusted Partner to leading businesses</PartnersHeading>
          </Column>
        </Row>
        <PartnersPanel />
      </PartnersContainer>
    </Section>

    <Section colour="white">
      <Container>
        <Row justifyContent="center">
          <GetInTouchButton />
        </Row>
      </Container>
    </Section>

    <Section colour="white" noTopPadding>
      <Container noPadding>
        <Row justifyContent="center" noMargin>
          <Column justifyContent="center">
            <DownloadAppPanel colour="blueGrey" />
          </Column>
        </Row>
      </Container>
    </Section>
  </>
);
